import { useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AccountSelect } from "@/components/controller/AccountSelect";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Trash2, AlertTriangle, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";

interface ExtractedLine {
  line_number?: number;
  date?: string;
  product_or_service?: string;
  description: string;
  quantity: number;
  unit?: string;
  unit_price: number;
  line_total: number;
  account_id?: string | null;
  suggested_account_id?: string | null;
}

interface ExtractedBill {
  fileName: string;
  filePath: string;
  billNumber: string;
  billDate: string;
  dueDate: string;
  terms: string;
  poNumber: string;
  projectName: string;
  vendorName: string;
  vendorAddress: string;
  vendorEmail: string;
  vendorPhone: string;
  billToName: string;
  billToCompany: string;
  billToAddress: string;
  lineItems: ExtractedLine[];
  subtotal: number;
  tax: number;
  discount: number;
  shipping: number;
  retainage: number;
  retainagePercent: number;
  amountPaid: number;
  balanceDue: number;
  notes: string;
  paymentMethods: string;
  matchedProjectId: string | null;
  needsProjectAssignment: boolean;
  selectedProjectId: string;
  sectionsData: Record<string, any> | null;
  saved: boolean;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

type Stage = "upload" | "extracting" | "review" | "saving";

export const BillUploadDialog = ({ open, onOpenChange, onSuccess }: Props) => {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>("upload");
  const [dragOver, setDragOver] = useState(false);

  // Batch extraction state
  const [extractionProgress, setExtractionProgress] = useState({ done: 0, total: 0, currentFile: "" });
  const [bills, setBills] = useState<ExtractedBill[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);

  // Fetch projects for selector
  const { data: projects } = useQuery({
    queryKey: ["projects_for_bill_link"],
    queryFn: async () => {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) return [];
      const { data } = await supabase
        .from("projects")
        .select("id, name, project_number")
        .eq("company_id", companyId)
        .order("name");
      return data ?? [];
    },
    enabled: open,
  });

  const reset = () => {
    setStage("upload");
    setDragOver(false);
    setExtractionProgress({ done: 0, total: 0, currentFile: "" });
    setBills([]);
    setCurrentIdx(0);
  };

  const handleClose = (o: boolean) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const extractSingleFile = async (file: File, companyId: string): Promise<ExtractedBill | null> => {
    const path = `${companyId}/${Date.now()}_${file.name}`;
    const { error: uploadErr } = await supabase.storage.from("bill-attachments").upload(path, file);
    if (uploadErr) {
      toast.error(`Upload failed for "${file.name}": ${uploadErr.message}`);
      return null;
    }

    const { data: fnData, error: fnErr } = await supabase.functions.invoke("extract-bill", {
      body: { file_path: path, company_id: companyId },
    });

    if (fnErr || fnData?.error) {
      toast.error(`Extraction failed for "${file.name}": ${fnErr?.message || fnData?.error}`);
      return null;
    }

    const d = fnData.data;
    return {
      fileName: file.name,
      filePath: path,
      billNumber: d.bill_number || "",
      billDate: d.bill_date || new Date().toISOString().slice(0, 10),
      dueDate: d.due_date || "",
      terms: d.terms || "",
      poNumber: d.po_number || "",
      projectName: d.project_name || "",
      vendorName: d.vendor_name || "",
      vendorAddress: d.vendor_address || "",
      vendorEmail: d.vendor_email || "",
      vendorPhone: d.vendor_phone || "",
      billToName: d.bill_to_name || "",
      billToCompany: d.bill_to_company || "",
      billToAddress: d.bill_to_address || "",
      lineItems: (d.line_items || []).map((l: any) => ({
        ...l,
        account_id: l.suggested_account_id || null,
      })),
      subtotal: d.subtotal || 0,
      tax: d.tax || 0,
      discount: d.discount || 0,
      shipping: d.shipping || 0,
      retainage: d.retainage || 0,
      retainagePercent: d.retainage_percent || 0,
      amountPaid: d.amount_paid || 0,
      balanceDue: d.balance_due || 0,
      notes: d.notes || "",
      paymentMethods: d.payment_methods || "",
      matchedProjectId: fnData.matched_project_id || null,
      needsProjectAssignment: fnData.needs_project_assignment ?? true,
      selectedProjectId: fnData.matched_project_id || "",
      sectionsData: fnData.sections || null,
      saved: false,
    };
  };

  const processFiles = async (fileList: File[]) => {
    const allowed = fileList.filter(f => {
      const ok = ["application/pdf", "image/png", "image/jpeg", "image/heic"].includes(f.type)
        || /\.(pdf|png|jpe?g|heic)$/i.test(f.name);
      if (!ok) toast.error(`"${f.name}" — unsupported file type`);
      return ok && f.size <= 50 * 1024 * 1024;
    });
    if (allowed.length === 0) return;

    setStage("extracting");
    setExtractionProgress({ done: 0, total: allowed.length, currentFile: allowed[0].name });

    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Could not determine company");

      const results: ExtractedBill[] = [];
      for (let i = 0; i < allowed.length; i++) {
        setExtractionProgress({ done: i, total: allowed.length, currentFile: allowed[i].name });
        const bill = await extractSingleFile(allowed[i], companyId);
        if (bill) results.push(bill);
      }

      if (results.length === 0) {
        toast.error("No bills could be extracted");
        setStage("upload");
        return;
      }

      setBills(results);
      setCurrentIdx(0);
      setStage("review");
      if (results.length > 1) {
        toast.success(`${results.length} bills extracted — review each one below`);
      }
    } catch (err: any) {
      toast.error(err.message || "Extraction failed");
      setStage("upload");
    }
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length) processFiles(files);
  }, []);

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) processFiles(files);
  };

  // Current bill helpers
  const bill = bills[currentIdx];

  const updateBill = (updates: Partial<ExtractedBill>) => {
    setBills(prev => prev.map((b, i) => i === currentIdx ? { ...b, ...updates } : b));
  };

  const updateLine = (idx: number, field: keyof ExtractedLine, value: string) => {
    if (!bill) return;
    const newLines = bill.lineItems.map((l, i) => {
      if (i !== idx) return l;
      if (field === "description" || field === "unit" || field === "product_or_service") return { ...l, [field]: value };
      const num = parseFloat(value) || 0;
      const updated = { ...l, [field]: num };
      if (field === "quantity" || field === "unit_price") {
        updated.line_total = updated.quantity * updated.unit_price;
      }
      return updated;
    });
    updateBill({ lineItems: newLines });
  };

  const removeLine = (idx: number) => {
    if (!bill) return;
    updateBill({ lineItems: bill.lineItems.filter((_, i) => i !== idx) });
  };

  const computedTotal = bill ? bill.lineItems.reduce((s, l) => s + (l.line_total || 0), 0) : 0;
  const finalTotal = bill ? computedTotal + bill.tax - bill.discount + bill.shipping - bill.retainage : 0;

  const saveCurrent = async () => {
    if (!bill || bill.lineItems.length === 0) return toast.error("Add at least one line item");
    setStage("saving");

    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Could not determine company");

      const projId = bill.selectedProjectId || null;
      const finalBillNumber = bill.billNumber || `BILL-${Date.now().toString(36).toUpperCase()}`;

      const { data: savedBill, error: billErr } = await supabase
        .from("bills")
        .insert({
          company_id: companyId,
          project_id: projId,
          bill_number: finalBillNumber,
          bill_date: bill.billDate || new Date().toISOString().slice(0, 10),
          due_date: bill.dueDate || null,
          subtotal: computedTotal,
          retainage_amount: bill.retainage,
          retainage_percent: bill.retainagePercent,
          total: finalTotal,
          description: bill.notes || null,
          status: "draft",
          source: "pdf_upload",
          pdf_path: bill.filePath,
          needs_project_assignment: !projId,
          extraction_status: "complete",
          payment_terms: bill.terms || null,
        } as any)
        .select("id")
        .single();

      if (billErr) throw new Error("Bill insert failed: " + billErr.message);

      const lines = bill.lineItems.map((l) => ({
        bill_id: savedBill!.id,
        company_id: companyId,
        description: l.description,
        quantity: l.quantity,
        unit_price: l.unit_price,
        line_total: l.line_total,
        account_id: l.account_id || null,
      }));

      if (lines.length > 0) {
        const { error: lineErr } = await supabase.from("bill_lines").insert(lines);
        if (lineErr) toast.error("Bill created but line items failed: " + lineErr.message);
      }

      if (bill.sectionsData) {
        const sectionRows = Object.entries(bill.sectionsData).map(([sectionType, data], idx) => ({
          bill_id: savedBill!.id,
          company_id: companyId,
          section_type: sectionType,
          section_data: data,
          display_order: idx,
        }));
        const { error: secErr } = await supabase.from("bill_sections").insert(sectionRows as any);
        if (secErr) console.error("Bill sections insert failed:", secErr.message);
      }

      // Mark as saved
      setBills(prev => prev.map((b, i) => i === currentIdx ? { ...b, saved: true } : b));

      const remaining = bills.filter((b, i) => !b.saved && i !== currentIdx);
      if (remaining.length > 0) {
        toast.success(`Bill "${finalBillNumber}" saved — ${remaining.length} remaining`);
        // Move to next unsaved
        const nextIdx = bills.findIndex((b, i) => i > currentIdx && !b.saved);
        setCurrentIdx(nextIdx >= 0 ? nextIdx : bills.findIndex((b, i) => i !== currentIdx && !b.saved));
        setStage("review");
      } else {
        toast.success(bills.length > 1 ? `All ${bills.length} bills imported successfully!` : `Bill ${finalBillNumber} imported successfully`);
        invalidateFinancials(qc);
        handleClose(false);
        onSuccess();
      }
    } catch (err: any) {
      toast.error(err.message || "Save failed");
      setStage("review");
    }
  };

  const savedCount = bills.filter(b => b.saved).length;
  const unsavedCount = bills.length - savedCount;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Upload Bill{bills.length > 1 ? "s" : ""}
            {stage === "review" && bills.length > 1 && (
              <Badge variant="secondary" className="ml-2 text-xs font-normal">
                {currentIdx + 1} of {bills.length} · {savedCount} saved
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {stage === "upload" && "Drag and drop PDF or image files, or click to browse. You can upload multiple bills at once."}
            {stage === "extracting" && `Extracting bill data… (${extractionProgress.done + 1} of ${extractionProgress.total})`}
            {stage === "review" && (bills.length > 1
              ? "Review each extracted bill and save them individually."
              : "Review the extracted data below and make corrections before saving.")}
            {stage === "saving" && "Saving bill…"}
          </DialogDescription>
        </DialogHeader>

        {/* Upload Stage */}
        {stage === "upload" && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm font-medium">Drop bills here or click to browse</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, PNG, JPG · Max 50 MB each · Multiple files supported</p>
            <input ref={fileRef} type="file" accept=".pdf,application/pdf,image/png,image/jpeg,.jpg,.jpeg,.png" multiple className="hidden" onChange={onFileSelect} />
          </div>
        )}

        {/* Extracting Stage */}
        {stage === "extracting" && (
          <div className="flex flex-col items-center gap-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="w-full max-w-xs">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-500 rounded-full"
                  style={{ width: `${((extractionProgress.done + 0.5) / extractionProgress.total) * 100}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">
                Extracting {extractionProgress.done + 1} of {extractionProgress.total}…
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span className="truncate max-w-[200px]">{extractionProgress.currentFile}</span>
            </div>
          </div>
        )}

        {/* Review Stage */}
        {stage === "review" && bill && (
          <div className="space-y-5">
            {/* Multi-bill navigation */}
            {bills.length > 1 && (
              <div className="flex items-center justify-between bg-muted/40 rounded-md px-3 py-2">
                <Button
                  variant="ghost" size="sm" disabled={currentIdx === 0}
                  onClick={() => setCurrentIdx(i => i - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <div className="flex items-center gap-1.5">
                  {bills.map((b, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentIdx(i)}
                      className={`w-7 h-7 rounded-full text-xs font-medium transition-colors ${
                        i === currentIdx
                          ? "bg-primary text-primary-foreground"
                          : b.saved
                          ? "bg-primary/20 text-primary"
                          : "bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/30"
                      }`}
                      title={b.fileName}
                    >
                      {b.saved ? <Check className="h-3 w-3 mx-auto" /> : i + 1}
                    </button>
                  ))}
                </div>
                <Button
                  variant="ghost" size="sm" disabled={currentIdx === bills.length - 1}
                  onClick={() => setCurrentIdx(i => i + 1)}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            )}

            {/* File name indicator */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span className="truncate">{bill.fileName}</span>
              {bill.saved && <Badge variant="secondary" className="text-xs">Saved</Badge>}
            </div>

            {/* Project Assignment */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project Assignment</Label>
                {bill.needsProjectAssignment && !bill.selectedProjectId && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/20">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Needs Assignment
                  </Badge>
                )}
                {bill.matchedProjectId && (
                  <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 dark:bg-green-950/20">
                    Auto-matched
                  </Badge>
                )}
              </div>
              <Select
                value={bill.selectedProjectId}
                onValueChange={v => updateBill({ selectedProjectId: v })}
                disabled={bill.saved}
              >
                <SelectTrigger><SelectValue placeholder="Select a project…" /></SelectTrigger>
                <SelectContent>
                  {projects?.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.project_number ? ` (${p.project_number})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Separator />

            {/* Header Fields */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Bill Number</Label>
                <Input value={bill.billNumber} onChange={e => updateBill({ billNumber: e.target.value })} placeholder="Auto-generate if blank" disabled={bill.saved} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Bill Date</Label>
                <Input type="date" value={bill.billDate} onChange={e => updateBill({ billDate: e.target.value })} disabled={bill.saved} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={bill.dueDate} onChange={e => updateBill({ dueDate: e.target.value })} disabled={bill.saved} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Terms</Label>
                <Input value={bill.terms} onChange={e => updateBill({ terms: e.target.value })} placeholder="e.g. Net 30" disabled={bill.saved} />
              </div>
            </div>

            {(bill.poNumber || bill.projectName) && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">PO Number</Label>
                  <Input value={bill.poNumber} onChange={e => updateBill({ poNumber: e.target.value })} disabled={bill.saved} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Project Name (from document)</Label>
                  <Input value={bill.projectName} readOnly className="bg-muted/50" />
                </div>
              </div>
            )}

            <Separator />

            {/* Vendor & Bill-To */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vendor / Payee</p>
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input value={bill.vendorName} onChange={e => updateBill({ vendorName: e.target.value })} disabled={bill.saved} />
                </div>
                {bill.vendorAddress && (
                  <div className="space-y-1">
                    <Label className="text-xs">Address</Label>
                    <Textarea className="min-h-[60px] text-xs" value={bill.vendorAddress} onChange={e => updateBill({ vendorAddress: e.target.value })} disabled={bill.saved} />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {bill.vendorEmail && (
                    <div className="space-y-1">
                      <Label className="text-xs">Email</Label>
                      <Input className="text-xs" value={bill.vendorEmail} onChange={e => updateBill({ vendorEmail: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                  {bill.vendorPhone && (
                    <div className="space-y-1">
                      <Label className="text-xs">Phone</Label>
                      <Input className="text-xs" value={bill.vendorPhone} onChange={e => updateBill({ vendorPhone: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                </div>
              </div>

              {(bill.billToName || bill.billToCompany || bill.billToAddress) && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bill To</p>
                  {bill.billToName && (
                    <div className="space-y-1">
                      <Label className="text-xs">Name</Label>
                      <Input className="text-xs" value={bill.billToName} onChange={e => updateBill({ billToName: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                  {bill.billToCompany && (
                    <div className="space-y-1">
                      <Label className="text-xs">Company</Label>
                      <Input className="text-xs" value={bill.billToCompany} onChange={e => updateBill({ billToCompany: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                  {bill.billToAddress && (
                    <div className="space-y-1">
                      <Label className="text-xs">Address</Label>
                      <Textarea className="min-h-[60px] text-xs" value={bill.billToAddress} onChange={e => updateBill({ billToAddress: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                </div>
              )}
            </div>

            <Separator />

            {/* Line Items */}
            <div className="space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Line Items</p>
              <div className="border rounded-md overflow-auto max-h-[300px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Description</TableHead>
                      <TableHead className="w-20">Qty</TableHead>
                      <TableHead className="w-28">Unit Price</TableHead>
                      <TableHead className="w-28">Total</TableHead>
                      <TableHead className="min-w-[180px]">Account</TableHead>
                      <TableHead className="w-10" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bill.lineItems.map((line, i) => (
                      <TableRow key={i}>
                        <TableCell className="p-1">
                          <Input className="h-8 text-xs" value={line.description} onChange={e => updateLine(i, "description", e.target.value)} disabled={bill.saved} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input className="h-8 text-xs text-right" type="number" value={line.quantity} onChange={e => updateLine(i, "quantity", e.target.value)} disabled={bill.saved} />
                        </TableCell>
                        <TableCell className="p-1">
                          <Input className="h-8 text-xs text-right" type="number" step="0.01" value={line.unit_price} onChange={e => updateLine(i, "unit_price", e.target.value)} disabled={bill.saved} />
                        </TableCell>
                        <TableCell className="p-1 text-right text-sm font-medium">{fmt(line.line_total)}</TableCell>
                        <TableCell className="p-1">
                          <AccountSelect
                            value={line.account_id || null}
                            onValueChange={(v) => {
                              const newLines = bill.lineItems.map((l, idx) => idx === i ? { ...l, account_id: v } : l);
                              updateBill({ lineItems: newLines });
                            }}
                            placeholder="Account"
                            className="h-8 text-xs"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeLine(i)} disabled={bill.saved}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-xs text-muted-foreground">{bill.lineItems.length} line item{bill.lineItems.length !== 1 ? "s" : ""}</p>
            </div>

            <Separator />

            {/* Totals */}
            <div className="flex justify-end">
              <div className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{fmt(computedTotal)}</span>
                </div>
                {bill.tax > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Tax</span>
                    <Input className="h-7 w-24 text-xs text-right" type="number" step="0.01" value={bill.tax} onChange={e => updateBill({ tax: parseFloat(e.target.value) || 0 })} disabled={bill.saved} />
                  </div>
                )}
                {bill.discount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-destructive">-{fmt(bill.discount)}</span>
                  </div>
                )}
                {bill.shipping > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Shipping</span>
                    <span>{fmt(bill.shipping)}</span>
                  </div>
                )}
                {bill.retainage > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Retainage{bill.retainagePercent ? ` (${bill.retainagePercent}%)` : ""}</span>
                    <span>-{fmt(bill.retainage)}</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Total</span>
                  <span>{fmt(finalTotal)}</span>
                </div>
                {bill.amountPaid > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>Amount Paid</span>
                    <span>-{fmt(bill.amountPaid)}</span>
                  </div>
                )}
                {(bill.balanceDue > 0 || bill.amountPaid > 0) && (
                  <div className="flex justify-between font-semibold text-primary">
                    <span>Balance Due</span>
                    <span>{fmt(bill.balanceDue || (finalTotal - bill.amountPaid))}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Notes & Payment */}
            {(bill.notes || bill.paymentMethods) && (
              <>
                <Separator />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {bill.notes && (
                    <div className="space-y-1">
                      <Label className="text-xs">Notes / Memo</Label>
                      <Textarea className="min-h-[60px] text-xs" value={bill.notes} onChange={e => updateBill({ notes: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                  {bill.paymentMethods && (
                    <div className="space-y-1">
                      <Label className="text-xs">Payment Methods</Label>
                      <Textarea className="min-h-[60px] text-xs" value={bill.paymentMethods} onChange={e => updateBill({ paymentMethods: e.target.value })} disabled={bill.saved} />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Saving Stage */}
        {stage === "saving" && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
            <span className="text-sm text-muted-foreground">Saving bill…</span>
          </div>
        )}

        {stage === "review" && bill && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancel</Button>
            {bill.saved ? (
              <Button disabled variant="secondary">
                <Check className="h-4 w-4 mr-1" /> Saved
              </Button>
            ) : (
              <Button onClick={saveCurrent}>
                {unsavedCount > 1 ? `Save & Continue (${unsavedCount} remaining)` : "Save Bill"}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
